import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { getWebAlertSoundUrl, getWebAlertVolume } from './utils/webAlertSound';
import Login from './pages/Login';
import ClaimPassword from './pages/Setup/ClaimPassword';
import SetupWizard from './pages/Setup/Wizard';
import ForgotPassword from './pages/ForgotPassword';
import DispatchIntegration from './pages/DispatchIntegration';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import { PermissionProvider } from './context/PermissionContext';
import Dashboard from './pages/Dashboard';
import Payments from './pages/Payments';
import Performance from './pages/Performance';
import Settings from './pages/Settings';
import ApplicationForm from './pages/ApplicationForm.js'
 import Submissions from './pages/Submissions';
 import Regions from './pages/Regions';
 import OwnerConsole from './pages/OwnerConsole'; // Website, marketing and hiring tools (owners only, password re-check)
 import LearnMore from './pages/LearnMore'; // Import the new LearnMore page
 import PaymentReports from './pages/PaymentReports'; // Import the new PaymentReports page
 import W9UploadPage from './components/W9UploadPage'; // Import W9UploadPage
 import RateAgreementPage from './components/RateAgreement/RateAgreementPage'; // Import RateAgreementPage
 import CoiUploadPage from './pages/CoiUploadPage'; // Import CoiUploadPage
 import BackgroundCheckUploadPage from './pages/BackgroundCheckUploadPage'; // Import BackgroundCheckUploadPage
import EmployeeBackgroundChecksPage from './pages/EmployeeBackgroundChecksPage';
 import InsuranceSubmission from './pages/InsuranceSubmission'; // Import InsuranceSubmission
 import DriverUpdatePage from './pages/DriverUpdatePage'; // Import DriverUpdatePage
 import SignPage from './pages/SignPage'; // Public customer sign-by-link page
 import PublicPageTheme from './components/PublicPageTheme'; // Fixed light theme for public pages (never inherit user theme)
 // Import only auth functions from authUtils
 import { isTokenExpired, verifyToken } from './utils/authUtils';
import { startIdleLogout } from './utils/idleLogout';
 // Import permission functions from permissionStore
 import { initializePermissions, canAccessPage } from './utils/permissionStore';
import { settingsAccess } from './utils/settingsAccess';
 import { initSocket, disconnectSocket } from './utils/socket';
 import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { NotificationProvider, useNotification, showGlobalNotification } from './context/NotificationContext';
import { LoadingProvider } from './context/LoadingContext'; // Import LoadingProvider
import { UserProvider } from './context/UserContext'; // Import UserProvider
import GlobalSpinner from './components/GlobalSpinner'; // Import GlobalSpinner
import Footer from './components/Footer'; // Import Footer component
import { Snackbar, Alert, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ThemeProvider } from './context/ThemeContext';
import { OrgProvider } from './context/OrgContext';
import ActAsBanner from './components/ActAsBanner';
import TourProvider from './tours/TourProvider'; // First-run page tours + the Help (?) menu state
import Help from './pages/Help'; // Written help and tutorials (/help)
import notificationSoundService from './services/notificationSoundService'; // Import notification sound service

// Pages reserved for White Knight owners and regional managers, whatever the stored page permissions say
const OWNER_RM_PAGES = ['submissions', 'regions'];
// The Owner Console is owners only (Org 1 OW/sOW); it is not in the stored page list at all
const OWNER_ONLY_PAGES = ['owner'];
// Open to every signed-in user, whatever the stored page permissions say
const EVERYONE_PAGES = ['help'];
const storedUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (e) { return null; }
};

// Protected route component that uses the permission system
const ProtectedRoute = ({ element, pageName }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [permissionsInitialized, setPermissionsInitialized] = useState(false);

  useEffect(() => {
    const checkAuthentication = async () => {
      // Check if user is authenticated - first try localStorage
      let token = localStorage.getItem('token');
      let userJson = localStorage.getItem('user');
      
      // If not in localStorage, check sessionStorage
      if (!token) {
        token = sessionStorage.getItem('backup_token');
        userJson = sessionStorage.getItem('backup_user');
        
        // If found in sessionStorage, restore to localStorage for consistency
        if (token) {
          // console.log('ProtectedRoute - Restoring token from sessionStorage backup'); // Removed log
          localStorage.setItem('token', token);
          
          if (userJson) {
            localStorage.setItem('user', userJson);
          }
        }
      }
      
      // console.log('ProtectedRoute - Token:', token ? 'exists' : 'missing'); // Removed log
      
      if (!token) {
        // console.log('ProtectedRoute - No token found in either storage location'); // Removed log
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      
      // Check if token is expired
      if (isTokenExpired(token)) {
        // console.log('ProtectedRoute - Token is expired'); // Removed log
        
        // Use the logout function to ensure consistent cleanup
        const { logout } = require('./utils/authUtils');
        
        // Call logout but prevent the redirect
        // We'll handle the redirect through the React Router
        logout();
        
        // If logout didn't redirect (because we're already at /login), 
        // we need to set authenticated state manually
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      
      // Verify token with server
      const isValid = await verifyToken();
      if (!isValid) {
        // console.log('ProtectedRoute - Token verification failed'); // Removed log
        // Clear all storage to prevent redirect loops
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('backup_token');
        sessionStorage.removeItem('backup_user');
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      
      // If we have a valid token, we're authenticated regardless of user data
      // This prevents redirect loops when token exists but user data is missing
      setIsAuthenticated(true);
      
      // Initialize permissions if not already done
      const permissions = initializePermissions();
      setPermissionsInitialized(!!permissions);
      
      setLoading(false);
    };
    
    checkAuthentication();
  }, []);

  // Determine the actual page name to check
  const actualPageName = pageName || window.location.pathname.replace('/', '') || 'dashboard';

  // console.log('ProtectedRoute - State:', { // Removed log
  //   isAuthenticated, // Removed log
  //   loading, // Removed log
  //   permissionsInitialized, // Removed log
  //   actualPageName // Removed log
  // }); // Removed log

  if (loading) {
    return <div>Loading...</div>;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    // console.log('ProtectedRoute - Not authenticated, redirecting to login'); // Removed log
    return <Navigate to="/login" />;
  }

  if (OWNER_RM_PAGES.includes(actualPageName.toLowerCase()) && !settingsAccess(storedUser()).ownerTools) {
    showGlobalNotification("You don't have access to that page", 'warning');
    return <Navigate to="/dashboard" />;
  }

  if (OWNER_ONLY_PAGES.includes(actualPageName.toLowerCase())) {
    if (settingsAccess(storedUser()).ownerConsole) return element;
    showGlobalNotification("You don't have access to that page", 'warning');
    return <Navigate to="/dashboard" />;
  }

  if (EVERYONE_PAGES.includes(actualPageName.toLowerCase())) return element;

  // Check if user has access to this page using the permission system
  const hasAccess = canAccessPage(actualPageName);

  // If user doesn't have access to this page, redirect to dashboard
  if (!hasAccess) {
    // console.log(`ProtectedRoute - User does not have access to ${actualPageName}`); // Removed log
    return <Navigate to="/dashboard" />;
  }

  // If authenticated and has access, render the component
  // console.log('ProtectedRoute - Rendering component'); // Removed log
  return element;
};

// Global notification component
const GlobalNotification = () => {
  const { notification, handleCloseNotification } = useNotification();
  
  // console.log('%c GlobalNotification: Rendering', 'background: #2196F3; color: white; padding: 2px 5px; border-radius: 2px;', notification); // Removed log
  
  // Log when the notification changes
  React.useEffect(() => {
    // console.log('GlobalNotification: notification state changed to:', notification); // Removed log
  }, [notification]);
  
  return (
    <Snackbar
      open={notification.show}
      autoHideDuration={10000}
      onClose={handleCloseNotification}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ 
        width: '100%',
        zIndex: 9999, // Ensure it appears on top of everything
        position: 'fixed', // Force fixed positioning
        top: 0,
        left: 0,
        right: 0,
        '& .MuiPaper-root': { 
          width: '100%', 
          maxWidth: '600px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)' // Stronger shadow
        }
      }}
    >
      <Alert 
        onClose={handleCloseNotification} 
        severity={notification.severity || 'info'} 
        variant="filled"
        sx={{ 
          width: '100%',
          fontSize: '1.1rem', // Larger text
          fontWeight: 'bold', // Bold text
          '& .MuiAlert-icon': {
            fontSize: '1.8rem' // Larger icon
          },
          '& .MuiAlert-action': {
            paddingTop: 0 // Align close button
          }
        }}
      >
        {notification.message || 'Notification'}
      </Alert>
    </Snackbar>
  );
};

// Test component to trigger notifications manually
const TestNotificationButton = () => {
  const { showNotification } = useNotification();
  const [audioUnlocked, setAudioUnlocked] = useState(
    localStorage.getItem('audioContextUnlocked') === 'true'
  );
  
  // Check if audio is unlocked
  useEffect(() => {
    const checkAudioUnlocked = () => {
      const isUnlocked = localStorage.getItem('audioContextUnlocked') === 'true';
      setAudioUnlocked(isUnlocked);
    };
    
    // Check initially
    checkAudioUnlocked();
    
    // Set up an interval to check periodically
    const intervalId = setInterval(checkAudioUnlocked, 2000);
    
    // Clean up
    return () => clearInterval(intervalId);
  }, []);
}; // Close TestNotificationButton component

  
// NotificationControls component and related handlers removed

function App() {
  // State to track if socket is initialized
  const [socketInitialized, setSocketInitialized] = useState(false);
  
  // Initialize notification sound service and service worker listener
  useEffect(() => {
    // Auto-initialize sound service if previously enabled
    notificationSoundService.autoInitialize();
    
    // Register service worker if not already registered
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          console.log('[App] Service Worker registered:', registration);
        })
        .catch(error => {
          console.error('[App] Service Worker registration failed:', error);
        });
    }
    
    // Listen for messages from service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'PUSH_RECEIVED') {
          console.log('[App] 🔔 Push notification received from SW:', event.data.notification);
          
          // Play notification sound if enabled
          if (notificationSoundService.isSoundEnabled()) {
            notificationSoundService.playNotificationBeep();
            console.log('[App] 🔊 Played notification sound for push');
          }
          
          // Show in-app notification
          const notification = event.data.notification;
          showGlobalNotification(
            notification.body || 'You have a new notification',
            'info'
          );
        }
      });
    }
    
    // Auto-enable sound on first user interaction
    const enableSoundOnInteraction = async () => {
      const shouldAutoEnable = localStorage.getItem('notificationSoundPendingEnable') === 'true' ||
                               localStorage.getItem('notificationSoundEnabled') === 'true';
      
      if (shouldAutoEnable && !notificationSoundService.isSoundEnabled()) {
        await notificationSoundService.enableSound();
        console.log('[App] 🔊 Auto-enabled notification sound on user interaction');
      }
      
      // Remove listeners after first interaction
      document.removeEventListener('click', enableSoundOnInteraction);
      document.removeEventListener('keydown', enableSoundOnInteraction);
      document.removeEventListener('touchstart', enableSoundOnInteraction);
    };
    
    // Add event listeners for user interaction
    document.addEventListener('click', enableSoundOnInteraction);
    document.addEventListener('keydown', enableSoundOnInteraction);
    document.addEventListener('touchstart', enableSoundOnInteraction);
    
    return () => {
      // Cleanup event listeners
      document.removeEventListener('click', enableSoundOnInteraction);
      document.removeEventListener('keydown', enableSoundOnInteraction);
      document.removeEventListener('touchstart', enableSoundOnInteraction);
    };
  }, []);
  
  // Initialize socket connection and permissions when app loads or when token changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // console.log('App: Initializing socket connection and permissions'); // Removed log
      
      // Initialize the permissions system
      const permissionsData = initializePermissions();
      if (permissionsData) {
        // console.log('App: Permissions initialized successfully'); // Removed log
      } else {
        // console.log('App: No permissions data available'); // Removed log
      }
      
      // Check if socket is already initialized to prevent duplicate connections
      try {
        const { getSocket } = require('./utils/socket');
        const existingSocket = getSocket();
        
        if (existingSocket && existingSocket.connected) {
          // console.log('App: Socket already connected, skipping initialization'); // Removed log
          setSocketInitialized(true);
          return;
        }
      } catch (error) {
        // If getSocket throws an error, it means the socket isn't initialized yet
        // console.log('App: No existing socket connection found, proceeding with initialization'); // Removed log
      }
      
      // Initialize the socket
      const socket = initSocket(token);
      
      // Store socket event handlers on window for direct access
      window.socketEventHandlers = {
        jobAssigned: (data) => {
          // console.log('App: Direct jobAssigned handler called with:', data); // Removed log
          
          // Use the global notification function which will handle sound playback
          // Add a small delay to ensure the notification context is ready
          setTimeout(() => {
            showGlobalNotification(
              `New job assigned: ${data.jobDetails.service} at ${data.jobDetails.location}`,
              'info',
              data.jobId
            );
          }, 100);
        }
      };
      
      setSocketInitialized(true);
      
      // Set up a listener for the socket-connected event
      const handleSocketConnected = (event) => {
        // console.log('App: Socket connected event received:', event.detail); // Removed log
        setSocketInitialized(true);
      };
      
      // Set up a listener for the socket-disconnected event
      const handleSocketDisconnected = (event) => {
        // Closing the socket on purpose (replacing it, logging out) is not a drop
        if (event?.detail?.reason === 'io client disconnect') return;

        // socket.io retries by itself after a network drop; only step in when it will not
        // (the server closed the connection, or its retries ran out) and only once at a time
        if (window.reconnectTimeoutId) clearTimeout(window.reconnectTimeoutId);
        window.reconnectTimeoutId = setTimeout(() => {
          delete window.reconnectTimeoutId;
          let current = null;
          try { current = require('./utils/socket').getSocket(); } catch (e) { /* none yet */ }
          if (current && (current.connected || current.active)) return; // already back, or still retrying
          initSocket(token);
        }, 5000);
      };
      
      // Add event listeners
      window.addEventListener('socket-connected', handleSocketConnected);
      window.addEventListener('socket-disconnected', handleSocketDisconnected);
      
      // Cleanup socket connection and event listeners when component unmounts or token changes
      return () => {
        // console.log('App: Cleaning up socket connection'); // Removed log
        
        // Clear any pending reconnect timeout
        if (window.reconnectTimeoutId) {
          clearTimeout(window.reconnectTimeoutId);
          delete window.reconnectTimeoutId;
        }
        
        // Disconnect the socket
        disconnectSocket();
        
        // Remove socket event handlers
        delete window.socketEventHandlers;
        
        // Remove event listeners
        window.removeEventListener('socket-connected', handleSocketConnected);
        window.removeEventListener('socket-disconnected', handleSocketDisconnected);
      };
    } else {
      // console.log('App: No token found, socket not initialized'); // Removed log
      setSocketInitialized(false);
    }
  }, []);
  
  // Check for pending notification sounds and handle notifications across tabs
  useEffect(() => {
    // Function to check and play pending notification sounds
    const checkPendingNotifications = () => {
      // console.log('App: Checking for pending notification sounds'); // Removed log
      
      // Check if there's a pending notification sound
      const hasPendingSound = sessionStorage.getItem('pendingNotificationSound') === 'true';
      
      if (hasPendingSound) {
        // console.log('App: Found pending notification sound, playing it now'); // Removed log
        
        // Get the stored events
        try {
          const storedEvents = JSON.parse(sessionStorage.getItem('socketEvents') || '[]');
          const unplayedEvents = storedEvents.filter(event => 
            event.type === 'jobAssigned' && event.soundPlayed === false
          );
          
          if (unplayedEvents.length > 0) {
            // console.log(`App: Found ${unplayedEvents.length} unplayed notification events`); // Removed log
            
            // Play notification sound
            try {
              const audio = new Audio(getWebAlertSoundUrl() || '/notification-sound.mp3');
              audio.volume = getWebAlertVolume();
              audio.play().catch(err => {
                // console.error('App: Error playing notification sound:', err); // Removed log
              });
            } catch (audioError) {
              // console.error('App: Error with audio playback:', audioError); // Removed log
            }
            
            // Mark all events as having played sound
            const updatedEvents = storedEvents.map(event => {
              if (event.type === 'jobAssigned' && !event.soundPlayed) {
                return { ...event, soundPlayed: true };
              }
              return event;
            });
            
            sessionStorage.setItem('socketEvents', JSON.stringify(updatedEvents));
            
            // Clear the pending notification sound flag
            sessionStorage.removeItem('pendingNotificationSound');
          }
        } catch (storageError) {
          // console.error('App: Error processing stored events:', storageError); // Removed log
        }
      }
    };
    
    // Check for pending notifications when the component mounts
    // This ensures that if a job was dispatched while the user was away,
    // the sound will play when they return to the app
    checkPendingNotifications();
    
    // Handle storage events for cross-tab notifications
    const handleStorageChange = (e) => {
      if (e.key === 'showNotification' && e.newValue === 'true') {
        // console.log('App: Detected showNotification flag in storage event'); // Removed log
        
        // Get the stored events
        try {
          const storedEvents = JSON.parse(sessionStorage.getItem('socketEvents') || '[]');
          if (storedEvents.length > 0) {
            // Get the most recent jobAssigned event
            const jobAssignedEvents = storedEvents.filter(event => event.type === 'jobAssigned');
            if (jobAssignedEvents.length > 0) {
              const latestEvent = jobAssignedEvents[jobAssignedEvents.length - 1];
              
              // Show notification for this event using the global function
              if (latestEvent.data && latestEvent.data.jobDetails) {
                showGlobalNotification(
                  `New job assigned: ${latestEvent.data.jobDetails.service} at ${latestEvent.data.jobDetails.location}`,
                  'info',
                  latestEvent.data.jobId
                );
              }
            }
          }
          
          // Clear the flag
          sessionStorage.removeItem('showNotification');
        } catch (storageError) {
          // console.error('Error processing stored events in storage event handler:', storageError); // Removed log
        }
      } else if (e.key === 'pendingNotificationSound' && e.newValue === 'true') {
        // If another tab sets the pendingNotificationSound flag, check for pending notifications
        checkPendingNotifications();
      }
    };
    
    // Add event listener
    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Without "Remember me", sign out after 30 minutes of inactivity or away from the site
  useEffect(() => startIdleLogout(), []);

  return (
   <OrgProvider> {/* Which company this portal is for (host or ?org=): name, logo, colors, before login */}
   <ThemeProvider>
    <CssBaseline />
    <UserProvider> {/* Wrap with UserProvider */}
      <NotificationProvider>
        <LoadingProvider>
          <PermissionProvider> {/* Add PermissionProvider */}
            <LocalizationProvider dateAdapter={AdapterDateFns}> {/* Add LocalizationProvider */}
            <div style={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <GlobalSpinner />
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatRoutes: true,
                  v7_relativeSplatPath: true
                }}
                style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <ActAsBanner /> {/* A White Knight owner acting as a partner company */}
                <TourProvider> {/* First-run tours: spotlight + card over whatever page is open */}
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Login />} />
                  <Route path="/login" element={<Login />} />
                  {/* A new partner owner's one-time setup link: sets their password, then the wizard */}
                  <Route path="/setup" element={<ClaimPassword />} />
                  <Route path="/setup/wizard" element={<SetupWizard />} />
                  <Route path="/forgot-password" element={<PublicPageTheme><ForgotPassword /></PublicPageTheme>} />
                  <Route path="/connect-towbook" element={<PublicPageTheme><DispatchIntegration /></PublicPageTheme>} />
                  <Route path="/reset-password/:token" element={<PublicPageTheme><ResetPassword /></PublicPageTheme>} />
                  <Route path="/register" element={<PublicPageTheme><Register /></PublicPageTheme>} />
                  <Route path="/learn-more" element={<LearnMore />} /> {/* Add route for LearnMore */}
                  <Route path="/w9-upload" element={<PublicPageTheme><W9UploadPage /></PublicPageTheme>} /> {/* Add route for W9UploadPage */}
                  <Route path="/rate-agreement/:token" element={<PublicPageTheme><RateAgreementPage /></PublicPageTheme>} /> {/* Add route for RateAgreementPage */}
                  <Route path="/upload-coi/:id" element={<PublicPageTheme><CoiUploadPage /></PublicPageTheme>} /> {/* Add route for CoiUploadPage */}
                  <Route path="/background-check-upload" element={<PublicPageTheme><BackgroundCheckUploadPage /></PublicPageTheme>} /> {/* Add route for BackgroundCheckUploadPage */}
                  <Route path="/background-checks/:id" element={<PublicPageTheme><EmployeeBackgroundChecksPage /></PublicPageTheme>} />
                  <Route path="/insurance-submission" element={<ProtectedRoute element={<InsuranceSubmission />} pageName="insurance-submission" />} /> {/* Add route for InsuranceSubmission */}
                  <Route path="/driver-updates/:token" element={<PublicPageTheme><DriverUpdatePage /></PublicPageTheme>} /> {/* Add route for DriverUpdatePage - public route for customer tracking */}
                  <Route path="/sign/:token" element={<PublicPageTheme><SignPage /></PublicPageTheme>} /> {/* Customer signs job terms from an SMS/email link */}

                  {/* Protected routes - use pageName for permission checks */}
                  <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} pageName="dashboard" />} />
                  <Route path="/payments" element={<ProtectedRoute element={<Payments />} pageName="payments" />} />
                  <Route path="/performance" element={<ProtectedRoute element={<Performance />} pageName="performance" />} />
                  <Route path="/settings" element={<ProtectedRoute element={<Settings />} pageName="settings" />} />
                  <Route path="/payment-reports" element={<ProtectedRoute element={<PaymentReports />} pageName="payment-reports" />} /> {/* Add route for PaymentReports */}
                  {/* Make ApplicationForm public */}
                  <Route path="/applicationform" element={<ApplicationForm />} />
                  <Route path="/submissions" element={<ProtectedRoute element={<Submissions />} pageName="submissions" />} />
                  <Route path="/regions" element={<ProtectedRoute element={<Regions />} pageName="regions" />} />
                  <Route path="/owner/*" element={<ProtectedRoute element={<OwnerConsole />} pageName="owner" />} />
                  <Route path="/help/*" element={<ProtectedRoute element={<Help />} pageName="help" />} />
                </Routes>
                </TourProvider>

                {/* Footer - only shown on authenticated routes */}
                {localStorage.getItem('token') && <Footer />}
              </Router>

              {/* Global notification */}
              <GlobalNotification />

              {/* TestNotificationButton component removed */}

              {/* Toast container for notifications */}
              <ToastContainer position="top-right" />
            </div>
            </LocalizationProvider> {/* Close LocalizationProvider */}
          </PermissionProvider> {/* Close PermissionProvider */}
        </LoadingProvider>
      </NotificationProvider>
    </UserProvider>
   </ThemeProvider>
   </OrgProvider>
  );
} // Re-adding closing brace for App function

export default App;
